import { defineStore } from 'pinia'
import { useCoreSocket } from '@/services/socket/core-socket'
import type { NestedObject } from '@/types'
import type { GeneralSummaryListItems } from '@/types'
import type { SharedUsageItem } from '@/schemas/general/types'
import {
  DEFAULT_QUANTITY_AREA,
  type ProjectCalculationValues,
  type Project_General_Data,
  type SocketProjectCalculationResultData,
  zod_Project_General_Data,
  zod_SharedUsageItem,
} from '@arcexperts/core-types'
import { renderGeneralSummary as renderGeneralSummaryPure } from '@/helpers/render-general-summary'
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectEngineerCalculator,
  useProjectElectricalCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'
import { deepClone, arrays_are_equal } from '@/helpers/objects_arrays'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

type GeneralCalculationResult = Extract<SocketProjectCalculationResultData, { part: 'general' }>

export interface State {
  projectGeneral: ProjectGeneralState | null
  previousObject: ProjectGeneralState | null
  buildingPlanCostFactor: number
  buildingPlanServiceFactor: number
  buildingPlanCostFactorsForUsages: number[]
  buildingPlanServiceFactorsForUsages: number[]
  buildingPlanCostFactorsForLandscapeUsages: number[]
  buildingPlanServiceFactorsForLandscapeUsages: number[]
  buildingPlanCostFactorsForRoleUsages: Partial<Record<Role, number[]>>
  buildingPlanServiceFactorsForRoleUsages: Partial<Record<Role, number[]>>
  usageFactorGeneration: number
  activeUsageFactorBatch: UsageFactorBatch | null
  pendingUsageFactorTargets: UsageFactorTargetKey[]
  regionFactor: number
}

type UsageFactorTargetKey = 'standard' | 'landscape' | Role

interface UsageFactorTarget {
  key: UsageFactorTargetKey
  usageCount: number
}

type FactorRequestUsageItem = SharedUsageStateItem & { role?: Role }

interface UsageFactorBatch {
  generation: number
  targets: UsageFactorTarget[]
  usageItems: FactorRequestUsageItem[]
  costFactors: number[] | null
  serviceFactors: number[] | null
}

type RequirementsLevel = 'LOW' | 'STANDARD' | 'HIGH' | 'VERY_HIGH'
const DEFAULT_REPETITION_FACTOR = 1.0
const REPETITION_FACTOR_MIN = 0.8
const REPETITION_FACTOR_MAX = 1.4
const DEFAULT_TECHNOLOGY_FACTOR = 1.0
const TECHNOLOGY_FACTOR_MIN = 0.8
const TECHNOLOGY_FACTOR_MAX = 1.4

export type SharedUsageStateItem = SharedUsageItem & {
  facadeEnvelope?: number
  buildingTypeId?: string | null
  buildingTypeName?: string | null
  requirementsLevel?: RequirementsLevel | string | null
  builtOverAreaRatio?: number | null
  unbuiltAreaRatio?: number | null
  aboveTerrainAreaRatio?: number | null
  belowTerrainAreaRatio?: number | null
  repetitionFactor?: number | null
  technologyFactor?: number | null
}

export type ProjectGeneralState = Omit<Project_General_Data, 'usageItems' | 'landscapeUsageItems'> & {
  canton?: string
  civilEngineerSlabArea?: number | null
  usageItems: SharedUsageStateItem[]
  landscapeUsageItems: SharedUsageStateItem[]
  roleUsageItems?: Partial<Record<Role, SharedUsageStateItem[]>>
}

function normalizeNullableString(value: unknown): string | null | undefined {
  return typeof value === 'string' || value === null ? value : undefined
}

function normalizeInterventionValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function normalizePercentageValue(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(100, Math.max(0, value))
}

function normalizeRepetitionFactor(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_REPETITION_FACTOR
  return Math.min(REPETITION_FACTOR_MAX, Math.max(REPETITION_FACTOR_MIN, value))
}

function normalizeTechnologyFactor(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_TECHNOLOGY_FACTOR
  return Math.min(TECHNOLOGY_FACTOR_MAX, Math.max(TECHNOLOGY_FACTOR_MIN, value))
}

function normalizeSharedUsageItem(item: unknown): SharedUsageStateItem {
  const raw = (item ?? {}) as Record<string, unknown>
  const normalized = zod_SharedUsageItem.parse(raw)

  return {
    ...normalized,
    facadeEnvelope: normalizeInterventionValue(raw.facadeEnvelope, normalized.rawConstruction),
    buildingTypeId: normalizeNullableString(raw.buildingTypeId),
    buildingTypeName: normalizeNullableString(raw.buildingTypeName),
    requirementsLevel: normalizeNullableString(raw.requirementsLevel),
    builtOverAreaRatio: normalizePercentageValue(raw.builtOverAreaRatio, 0),
    unbuiltAreaRatio: normalizePercentageValue(raw.unbuiltAreaRatio, 100),
    aboveTerrainAreaRatio: normalizePercentageValue(raw.aboveTerrainAreaRatio, 100),
    belowTerrainAreaRatio: normalizePercentageValue(raw.belowTerrainAreaRatio, 0),
    repetitionFactor: normalizeRepetitionFactor(raw.repetitionFactor),
    technologyFactor: normalizeTechnologyFactor(raw.technologyFactor),
  }
}

function normalizeProjectGeneralData(data: NestedObject | ProjectGeneralState | undefined): ProjectGeneralState | null {
  if (!data) return null

  const raw = data as Record<string, unknown>
  const normalized = zod_Project_General_Data.parse(raw)
  const usageItems = Array.isArray(raw.usageItems) ? raw.usageItems.map(normalizeSharedUsageItem) : []
  const landscapeUsageItems = Array.isArray(raw.landscapeUsageItems)
    ? raw.landscapeUsageItems.map(normalizeSharedUsageItem)
    : []
  const roleUsageItems = Object.fromEntries(
    Object.entries((raw.roleUsageItems ?? {}) as Record<string, unknown>).flatMap(([role, items]) =>
      Array.isArray(items) ? [[role, items.map(normalizeSharedUsageItem)]] : []
    )
  ) as Partial<Record<Role, SharedUsageStateItem[]>>

  return {
    ...normalized,
    canton: typeof raw.canton === 'string' ? raw.canton : undefined,
    civilEngineerSlabArea:
      typeof raw.civilEngineerSlabArea === 'number' && Number.isFinite(raw.civilEngineerSlabArea)
        ? raw.civilEngineerSlabArea
        : null,
    usageItems,
    landscapeUsageItems,
    roleUsageItems,
  }
}

function getFallbackUsageItemsForRole(state: State, role: Role): SharedUsageStateItem[] {
  return role === 'LANDSCAPE_ARCHITECT'
    ? (state.projectGeneral?.landscapeUsageItems ?? [])
    : (state.projectGeneral?.usageItems ?? [])
}

function mergeRoleUsageItemsForRole(state: State, role: Role): SharedUsageStateItem[] {
  const baseUsageItems = getFallbackUsageItemsForRole(state, role)
  const roleUsageItems = state.projectGeneral?.roleUsageItems?.[role]
  if (!roleUsageItems?.length) return baseUsageItems

  return baseUsageItems.map((baseUsage, index) => {
    const roleUsage = roleUsageItems[index]
    if (!roleUsage) return baseUsage

    return {
      ...baseUsage,
      ...roleUsage,
      type: baseUsage.type,
      typeCounter: baseUsage.typeCounter,
      ratio: baseUsage.ratio,
      buildingTypeId: baseUsage.buildingTypeId,
      buildingTypeName: baseUsage.buildingTypeName,
      builtOverAreaRatio: baseUsage.builtOverAreaRatio,
      unbuiltAreaRatio: baseUsage.unbuiltAreaRatio,
      aboveTerrainAreaRatio: baseUsage.aboveTerrainAreaRatio,
      belowTerrainAreaRatio: baseUsage.belowTerrainAreaRatio,
    }
  })
}

function getUsageItemsForFactorTarget(state: State, key: UsageFactorTargetKey): SharedUsageStateItem[] {
  if (key === 'standard') {
    return state.projectGeneral?.usageItems ?? []
  }
  if (key === 'landscape') {
    return state.projectGeneral?.landscapeUsageItems ?? []
  }
  return mergeRoleUsageItemsForRole(state, key)
}

/* Role the factors are computed for; enables role-specific factors (e.g. extension factor).
   Landscape usage items are only consumed by the landscape architect role. */
function getRoleForFactorTarget(key: UsageFactorTargetKey): Role | null {
  if (key === 'standard') return null
  if (key === 'landscape') return 'LANDSCAPE_ARCHITECT'
  return key
}

function getChangedRoleUsageItems(
  current: Partial<Record<Role, SharedUsageStateItem[]>>,
  previous: Partial<Record<Role, SharedUsageStateItem[]>>
): Role[] {
  const roles = new Set([...Object.keys(current), ...Object.keys(previous)] as Role[])
  return [...roles].filter((role) => !arrays_are_equal(current[role] ?? [], previous[role] ?? []))
}

function buildGeneralUsagePayload(
  usageItems: FactorRequestUsageItem[]
): ProjectCalculationValues<'general', 'buildingPlanCostFactorsForUsages'> {
  return {
    usageItems,
  }
}

function buildRegionFactorPayload(
  projectGeneral: ProjectGeneralState
): ProjectCalculationValues<'general', 'regionFactor'> {
  return {
    country: projectGeneral.country,
    canton: projectGeneral.canton,
  } as ProjectCalculationValues<'general', 'regionFactor'>
}

export const useProjectGeneralCalculator = defineStore('projectGeneralCalculator', {
  state: (): State => ({
    projectGeneral: null,
    previousObject: null,
    buildingPlanCostFactor: 1,
    buildingPlanServiceFactor: 1,
    buildingPlanCostFactorsForUsages: [],
    buildingPlanServiceFactorsForUsages: [],
    buildingPlanCostFactorsForLandscapeUsages: [],
    buildingPlanServiceFactorsForLandscapeUsages: [],
    buildingPlanCostFactorsForRoleUsages: {},
    buildingPlanServiceFactorsForRoleUsages: {},
    usageFactorGeneration: 0,
    activeUsageFactorBatch: null,
    pendingUsageFactorTargets: [],
    regionFactor: 1,
  }),
  getters: {
    getProjectGeneral: (state) => state.projectGeneral,
    getRegionFactor: (state) => state.regionFactor,
    getBuildingPlanCostFactor: (state) => state.buildingPlanCostFactor,
    getBuildingPlanServiceFactor: (state) => state.buildingPlanServiceFactor,
    getBuildingPlanCostFactorsForUsages: (state) => state.buildingPlanCostFactorsForUsages,
    getBuildingPlanServiceFactorsForUsages: (state) => state.buildingPlanServiceFactorsForUsages,
    getBuildingPlanCostFactorsForLandscapeUsages: (state) => state.buildingPlanCostFactorsForLandscapeUsages,
    getBuildingPlanServiceFactorsForLandscapeUsages: (state) => state.buildingPlanServiceFactorsForLandscapeUsages,
    getBuildingPlanCostFactorsForRoleUsages:
      (state) =>
      (role: Role): number[] | undefined =>
        state.buildingPlanCostFactorsForRoleUsages[role],
    getBuildingPlanServiceFactorsForRoleUsages:
      (state) =>
      (role: Role): number[] | undefined =>
        state.buildingPlanServiceFactorsForRoleUsages[role],
    getQuantityArea: (state): number => state.projectGeneral?.quantityArea ?? DEFAULT_QUANTITY_AREA,
    getCivilEngineerSlabArea: (state): number | null => state.projectGeneral?.civilEngineerSlabArea ?? null,
    getSharedUsages: (state): SharedUsageStateItem[] => state.projectGeneral?.usageItems ?? [],
    getSharedUsagesForRole:
      (state) =>
      (role: Role): SharedUsageStateItem[] =>
        mergeRoleUsageItemsForRole(state, role),
    getLandscapeQuantityArea: (state): number => state.projectGeneral?.landscapeQuantityArea ?? DEFAULT_QUANTITY_AREA,
    getLandscapeSharedUsages: (state): SharedUsageStateItem[] => state.projectGeneral?.landscapeUsageItems ?? [],
    renderGeneralSummary:
      (state) =>
      (
        t: (key: string) => string,
        wt: (...path: string[]) => string,
        wtSummary: (...path: string[]) => string
      ): GeneralSummaryListItems => {
        return renderGeneralSummaryPure({ projectGeneral: state.projectGeneral }, t, wt, wtSummary)
      },
  },
  actions: {
    init(data: NestedObject | ProjectGeneralState) {
      const normalized = normalizeProjectGeneralData(data)
      if (!normalized) return

      this.$patch({ projectGeneral: normalized, previousObject: deepClone(normalized) })
      this.scheduleUsageFactorUpdates([
        'standard',
        'landscape',
        ...(Object.keys(normalized.roleUsageItems ?? {}) as Role[]),
      ])
      this.setRegionFactor()
    },
    setGeneralData(data: NestedObject | ProjectGeneralState | undefined) {
      const normalized = normalizeProjectGeneralData(data)
      if (!normalized) return

      const previousRoleUsageItems = this.previousObject?.roleUsageItems ?? {}
      const setRegionFactor = normalized.canton !== this.previousObject?.canton
      const changedCompanySideOption = normalized.companySideOption !== this.previousObject?.companySideOption
      const changedQuantityArea = normalized.quantityArea !== this.previousObject?.quantityArea
      const changedCivilEngineerSlabArea =
        normalized.civilEngineerSlabArea !== this.previousObject?.civilEngineerSlabArea
      const changedUsageItems = !arrays_are_equal(normalized.usageItems, this.previousObject?.usageItems)
      const changedLandscapeQuantityArea =
        normalized.landscapeQuantityArea !== this.previousObject?.landscapeQuantityArea
      const changedLandscapeUsageItems = !arrays_are_equal(
        normalized.landscapeUsageItems,
        this.previousObject?.landscapeUsageItems
      )
      const changedRoleUsageItems = getChangedRoleUsageItems(normalized.roleUsageItems ?? {}, previousRoleUsageItems)

      this.$patch({ projectGeneral: normalized, previousObject: deepClone(normalized) })

      if (setRegionFactor) {
        this.setRegionFactor()
      }
      if (changedCompanySideOption) {
        // companySideOption (e.g. INDIVIDUAL_SERVICE) affects architect service defaults and percentages.
        // Force reload of default phases and percentage ranges on the architect calculator.
        const architectCalc = useProjectArchitectCalculator()
        architectCalc.setInitialDefaultPhase(true)
        architectCalc.setPercentageRangeForPhases(true)
      }

      const factorTargets: UsageFactorTargetKey[] = []
      if (changedUsageItems) {
        factorTargets.push('standard')
        factorTargets.push(...(Object.keys(normalized.roleUsageItems ?? {}) as Role[]))
      } else if (changedQuantityArea) {
        this.notifyStandardRoleCalculatorsOfSharedUsageChange()
      }

      if (changedLandscapeUsageItems) {
        factorTargets.push('landscape')
        if (normalized.roleUsageItems?.LANDSCAPE_ARCHITECT) {
          factorTargets.push('LANDSCAPE_ARCHITECT')
        }
      } else if (changedLandscapeQuantityArea) {
        this.notifyLandscapeCalculatorOfSharedUsageChange()
      }

      factorTargets.push(...changedRoleUsageItems)
      if (factorTargets.length) {
        this.scheduleUsageFactorUpdates(factorTargets)
      }

      if (
        changedCivilEngineerSlabArea &&
        !changedQuantityArea &&
        !changedUsageItems &&
        !changedRoleUsageItems.includes('CIVIL_ENGINEER')
      ) {
        useProjectCivilEngineerCalculator().onSharedUsagesChanged()
      }
    },
    /**
     * Notifies all role calculators when shared usages or quantityArea change in General.
     * This triggers recalculation of all setup-related values.
     */
    notifyRoleCalculatorsOfSharedUsageChange() {
      this.notifyStandardRoleCalculatorsOfSharedUsageChange()
      this.notifyLandscapeCalculatorOfSharedUsageChange()
    },
    /**
     * Notifies the standard role calculators, which derive from usageItems/quantityArea.
     * The landscape architect calculator derives from landscapeUsageItems/landscapeQuantityArea instead.
     */
    notifyStandardRoleCalculatorsOfSharedUsageChange(excludedRoles: Role[] = []) {
      const excluded = new Set(excludedRoles)
      const architectCalc = useProjectArchitectCalculator()
      const civilEngineerCalc = useProjectCivilEngineerCalculator()
      const civilEngineerExcavationCalc = useProjectCivilEngineerExcavationCalculator()
      const engineerCalc = useProjectEngineerCalculator()
      const electricalCalc = useProjectElectricalCalculator()
      const heatingCoolingCalc = useProjectHeatingCoolingCalculator()
      const ventilationACCalc = useProjectVentilationACCalculator()
      const hvacCalc = useProjectHVACCalculator()
      const sanitaryCalc = useProjectSanitaryCalculator()
      const buildingAutomationCalc = useProjectBuildingAutomationCalculator()
      const specialistCoordinationCalc = useProjectSpecialistCoordinationCalculator()

      if (!excluded.has('ARCHITECT')) architectCalc.onSharedUsagesChanged()
      if (!excluded.has('CIVIL_ENGINEER')) civilEngineerCalc.onSharedUsagesChanged()
      if (!excluded.has('CIVIL_ENGINEER_EXCAVATION')) civilEngineerExcavationCalc.onSharedUsagesChanged()
      if (!excluded.has('ENGINEER')) engineerCalc.onSharedUsagesChanged()
      if (!excluded.has('ELECTRICAL')) electricalCalc.onSharedUsagesChanged()
      if (!excluded.has('HEATING_COOLING')) heatingCoolingCalc.onSharedUsagesChanged()
      if (!excluded.has('VENTILATION_AC')) ventilationACCalc.onSharedUsagesChanged()
      if (!excluded.has('HVAC')) hvacCalc.onSharedUsagesChanged()
      if (!excluded.has('SANITARY')) sanitaryCalc.onSharedUsagesChanged()
      if (!excluded.has('BUILDING_AUTOMATION')) buildingAutomationCalc.onSharedUsagesChanged()
      if (!excluded.has('SPECIALIST_COORDINATION')) specialistCoordinationCalc.onSharedUsagesChanged()
    },
    notifyLandscapeCalculatorOfSharedUsageChange() {
      useProjectLandscapeArchitectCalculator().onSharedUsagesChanged()
    },
    notifyRoleCalculatorOfSharedUsageChange(role: Role) {
      switch (role) {
        case 'ARCHITECT':
          useProjectArchitectCalculator().onSharedUsagesChanged()
          return
        case 'LANDSCAPE_ARCHITECT':
          useProjectLandscapeArchitectCalculator().onSharedUsagesChanged()
          return
        case 'CIVIL_ENGINEER':
          useProjectCivilEngineerCalculator().onSharedUsagesChanged()
          return
        case 'CIVIL_ENGINEER_EXCAVATION':
          useProjectCivilEngineerExcavationCalculator().onSharedUsagesChanged()
          return
        case 'ENGINEER':
          useProjectEngineerCalculator().onSharedUsagesChanged()
          return
        case 'ELECTRICAL':
          useProjectElectricalCalculator().onSharedUsagesChanged()
          return
        case 'HEATING_COOLING':
          useProjectHeatingCoolingCalculator().onSharedUsagesChanged()
          return
        case 'VENTILATION_AC':
          useProjectVentilationACCalculator().onSharedUsagesChanged()
          return
        case 'HVAC':
          useProjectHVACCalculator().onSharedUsagesChanged()
          return
        case 'SANITARY':
          useProjectSanitaryCalculator().onSharedUsagesChanged()
          return
        case 'BUILDING_AUTOMATION':
          useProjectBuildingAutomationCalculator().onSharedUsagesChanged()
          return
        case 'SPECIALIST_COORDINATION':
          useProjectSpecialistCoordinationCalculator().onSharedUsagesChanged()
      }
    },
    setBuildingPlanCostFactor() {
      this.$patch({ buildingPlanCostFactor: 1 })
    },
    setBuildingPlanServiceFactor() {
      this.$patch({ buildingPlanServiceFactor: 1 })
    },
    scheduleUsageFactorUpdates(targets: UsageFactorTargetKey[]) {
      const uniqueTargets = [...new Set(targets)]
      if (!uniqueTargets.length) return

      this.usageFactorGeneration += 1
      if (this.activeUsageFactorBatch) {
        this.pendingUsageFactorTargets = [...new Set([...this.pendingUsageFactorTargets, ...uniqueTargets])]
        return
      }

      this.startUsageFactorBatch(uniqueTargets)
    },
    startUsageFactorBatch(targetKeys: UsageFactorTargetKey[]) {
      const targets: UsageFactorTarget[] = []
      const usageItems: FactorRequestUsageItem[] = []

      for (const key of targetKeys) {
        const targetRole = getRoleForFactorTarget(key)
        const targetUsageItems = getUsageItemsForFactorTarget(this.$state, key).map((item) =>
          targetRole ? { ...item, role: targetRole } : item
        )
        targets.push({ key, usageCount: targetUsageItems.length })
        usageItems.push(...targetUsageItems)
      }

      this.activeUsageFactorBatch = {
        generation: this.usageFactorGeneration,
        targets,
        usageItems,
        costFactors: null,
        serviceFactors: null,
      }

      if (!usageItems.length) {
        this.activeUsageFactorBatch.costFactors = []
        this.activeUsageFactorBatch.serviceFactors = []
        this.completeUsageFactorBatch()
        return
      }

      useCoreSocket().messageCenter?.triggerProjectCalculation(
        'general',
        'buildingPlanCostFactorsForUsages',
        buildGeneralUsagePayload(usageItems)
      )
      useCoreSocket().messageCenter?.triggerProjectCalculation(
        'general',
        'buildingPlanServiceFactorsForUsages',
        buildGeneralUsagePayload(usageItems)
      )
    },
    completeUsageFactorBatch() {
      const batch = this.activeUsageFactorBatch
      if (!batch?.costFactors || !batch.serviceFactors) return

      const pendingTargets = new Set(this.pendingUsageFactorTargets)
      const excludedRoles = new Set<Role>()
      for (const target of [...batch.targets, ...this.pendingUsageFactorTargets.map((key) => ({ key }))]) {
        if (target.key !== 'standard' && target.key !== 'landscape') {
          excludedRoles.add(target.key)
        }
      }

      const targetsToNotify: UsageFactorTargetKey[] = []
      let offset = 0
      for (const target of batch.targets) {
        const nextOffset = offset + target.usageCount
        const costFactors = batch.costFactors.slice(offset, nextOffset)
        const serviceFactors = batch.serviceFactors.slice(offset, nextOffset)
        offset = nextOffset

        if (pendingTargets.has(target.key)) {
          continue
        }

        if (target.key === 'standard') {
          this.buildingPlanCostFactorsForUsages = costFactors
          this.buildingPlanServiceFactorsForUsages = serviceFactors
        } else if (target.key === 'landscape') {
          this.buildingPlanCostFactorsForLandscapeUsages = costFactors
          this.buildingPlanServiceFactorsForLandscapeUsages = serviceFactors
        } else {
          this.buildingPlanCostFactorsForRoleUsages = {
            ...this.buildingPlanCostFactorsForRoleUsages,
            [target.key]: costFactors,
          }
          this.buildingPlanServiceFactorsForRoleUsages = {
            ...this.buildingPlanServiceFactorsForRoleUsages,
            [target.key]: serviceFactors,
          }
        }
        targetsToNotify.push(target.key)
      }

      this.activeUsageFactorBatch = null

      if (targetsToNotify.includes('standard')) {
        this.notifyStandardRoleCalculatorsOfSharedUsageChange([...excludedRoles])
      }
      if (targetsToNotify.includes('landscape') && !excludedRoles.has('LANDSCAPE_ARCHITECT')) {
        this.notifyLandscapeCalculatorOfSharedUsageChange()
      }
      for (const target of targetsToNotify) {
        if (target !== 'standard' && target !== 'landscape') {
          this.notifyRoleCalculatorOfSharedUsageChange(target)
        }
      }

      if (this.pendingUsageFactorTargets.length) {
        const nextTargets = this.pendingUsageFactorTargets
        this.pendingUsageFactorTargets = []
        this.startUsageFactorBatch(nextTargets)
      }
    },
    setRegionFactor() {
      if (this.projectGeneral?.canton) {
        useCoreSocket().messageCenter?.triggerProjectCalculation(
          'general',
          'regionFactor',
          buildRegionFactorPayload(this.projectGeneral)
        )
      }
    },
    setResult(result: GeneralCalculationResult) {
      switch (result.request) {
        case 'buildingPlanCostFactor':
          this.buildingPlanCostFactor = result.value
          this.notifyRoleCalculatorsOfSharedUsageChange()
          return
        case 'buildingPlanServiceFactor':
          this.buildingPlanServiceFactor = result.value
          this.notifyRoleCalculatorsOfSharedUsageChange()
          return
        case 'buildingPlanCostFactorsForUsages':
          if (this.activeUsageFactorBatch) {
            this.activeUsageFactorBatch.costFactors = result.value
            this.completeUsageFactorBatch()
          } else {
            this.buildingPlanCostFactorsForUsages = result.value
          }
          return
        case 'buildingPlanServiceFactorsForUsages':
          if (this.activeUsageFactorBatch) {
            this.activeUsageFactorBatch.serviceFactors = result.value
            this.completeUsageFactorBatch()
          } else {
            this.buildingPlanServiceFactorsForUsages = result.value
          }
          return
        case 'buildingPlanCostFactorsForLandscapeUsages':
          this.buildingPlanCostFactorsForLandscapeUsages = result.value
          this.notifyLandscapeCalculatorOfSharedUsageChange()
          return
        case 'buildingPlanServiceFactorsForLandscapeUsages':
          this.buildingPlanServiceFactorsForLandscapeUsages = result.value
          this.notifyLandscapeCalculatorOfSharedUsageChange()
          return
        case 'regionFactor':
          this.regionFactor = result.value
      }
    },
  },
})
